import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useTranslation } from 'react-i18next';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UpgradeModal({ isOpen, onClose }: UpgradeModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            priceId: 'price_1QpqxqP3pVbj0Aq0sFxqxqxq',
            successUrl: `${window.location.origin}/dashboard?success=true`,
            cancelUrl: `${window.location.origin}/dashboard`,
          }),
        }
      );

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-3xl font-bold text-gray-900">{t('pricing.title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <div className="border-2 border-gray-200 rounded-xl p-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
                <div className="flex items-end justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">$0</span>
                </div>
                <p className="text-sm text-gray-600">{t('plan.pro.period')}</p>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">바이오 링크 3개</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">QR 코드 5개</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">짧은 링크 10개</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">기본 분석</span>
                </li>
              </ul>

              <button
                disabled
                className="w-full py-3 rounded-xl font-semibold whitespace-nowrap bg-gray-100 text-gray-400 cursor-not-allowed"
              >
                현재 플랜
              </button>
            </div>

            {/* Pro Plan */}
            <div className="relative border-2 border-teal-500 rounded-xl p-6 shadow-lg shadow-teal-500/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                  Best Value
                </div>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('plan.pro.name')}</h3>
                <div className="flex items-end justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">{t('plan.pro.price')}</span>
                </div>
                <p className="text-sm text-gray-600">{t('plan.pro.period')}</p>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{t('plan.pro.feature1')}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{t('plan.pro.feature2')}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{t('plan.pro.feature3')}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{t('plan.pro.feature4')}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{t('plan.pro.feature5')}</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{t('plan.pro.feature6')}</span>
                </li>
              </ul>

              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:shadow-lg hover:shadow-teal-500/30 disabled:opacity-50"
              >
                {loading ? '처리 중...' : t('plan.pro.cta')}
              </button>
            </div>
          </div>

          <div className="mt-8 p-4 bg-teal-50 rounded-xl">
            <div className="flex items-start">
              <i className="ri-information-line text-teal-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">안전한 결제</p>
                <p>Stripe를 통한 안전한 결제 처리. 언제든지 취소 가능합니다.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
